import React from 'react';
import { Inspector, List, Tabs, Thumbnail } from '@bynder/design-system';
import {
  SetHomepageToggle,
  PublishLandingPageToggle,
  PublishAudiencePageToggle,
} from '../PageSettings/PageSettings';

import {
  PERMISSION_ENTITIES,
  PORTAL_SETTINGS_PERMISSIONS,
} from '../../../../helpers/permissions.ts';
import { AbilityContext } from '../../../../context/ability.ts';
import { InspectorTitleEditor } from '../InspectorTitleEditor.tsx';
import { useGuideMutations } from '../../../../mutations/guides.ts';
import { usePageMutations } from '../../../../mutations/pages.ts';
import { InspectorThemesetEditor } from '../InspectorThemesetEditor.tsx';
import { IconCopy, IconDelete, IconTarget } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../../../constants/app/index.ts';
import { ModalType } from '../../../../types/modals.ts';
import { useModalStore } from '../../../../stores/modalStore.ts';
import {
  ActiveSplits,
  useReleaseToggle,
} from '../../../../helpers/releaseToggle.ts';
import {
  HomepagePermissions,
  useHasAllPermissions,
} from '../../../../helpers/hooks.ts';
import { useActiveGuide } from '../../../../queries/guides.ts';
import { generateUUID } from '../../../../helpers/utils.ts';

export const HomePageSettings: React.FC<{
  isActivePagePublished: boolean;
  isLastHomepagePageActive: boolean;
  guideId: string;
  pageId: string;
  guideTitle: string;
  pageTitle: string;
  defaultPageId: string;
  targetedPagesEnabled: boolean;
}> = ({
  isActivePagePublished,
  isLastHomepagePageActive,
  guideId,
  pageId,
  guideTitle,
  pageTitle,
  defaultPageId,
  targetedPagesEnabled,
}) => {
  const { translate } = useTranslate();
  const history = useHistory();
  const abilities = React.useContext(AbilityContext);
  const { editGuideTitle, enableTargetedPages } = useGuideMutations(guideId);
  const openModal = useModalStore.use.openModal();
  const { editPageTitle, deletePage, duplicatePage } = usePageMutations();
  const { data: guide } = useActiveGuide();
  const { data: isPageInPageEnabled } = useReleaseToggle(
    ActiveSplits.pageInPage,
  );

  const canManagePortalSettings = abilities.can(
    PORTAL_SETTINGS_PERMISSIONS.MANAGE,
    PERMISSION_ENTITIES.PORTAL_SETTINGS,
  );

  const hasHomePageManage = useHasAllPermissions(HomepagePermissions.MANAGE);

  const handleEditPageTitle = (newTitle: string) => {
    if (!newTitle) return;

    editPageTitle.mutate({ newTitle, pageId });
  };

  const handleEditGuideTitle = (guideTitle: string) => {
    if (!guideTitle) return;

    editGuideTitle.mutate({ guideTitle });
  };

  const handleToggleTargetedPages = () => {
    if (targetedPagesEnabled) {
      openModal({
        modalType: ModalType.TARGETED_PAGES_MODAL,
        modalOnSubmit: () => {},
      });
    } else {
      enableTargetedPages.mutate({ guideId });
    }
  };

  const handleDeletePage = () => {
    const redirectAndDelete = () => {
      history.push(ROUTES.GUIDE_PAGE_EDIT(guideId, defaultPageId));
      deletePage.mutate({ pageId });
    };

    if (!isLastHomepagePageActive) {
      const modalProps = {
        headerText: translate('DELETE_PAGE_MODAL.HEADER_TEXT'),
        contentText: translate('DELETE_PAGE_MODAL.CONTENT_TEXT', {
          title: pageTitle || '',
        }),
        checkbox1Text: translate('DELETE_GUIDE_MODAL.CHECKBOX_1_TEXT'),
        checkbox2Text: translate('DELETE_GUIDE_MODAL.CHECKBOX_2_TEXT'),
        buttonText: translate('DELETE_PAGE_MODAL.BUTTON_TEXT'),
      };
      openModal({
        modalType: ModalType.DELETE_MODAL,
        modalOnSubmit: redirectAndDelete,
        modalProps,
      });
    }
  };

  const handleDuplicatePage = () => {
    // If last page is active, copy guide title, otherwise copy page title
    const guideTitle = isLastHomepagePageActive
      ? guide.title + translate('DUPLICATE_PAGE_MODAL.COPY')
      : pageTitle + translate('DUPLICATE_PAGE_MODAL.COPY');

    duplicatePage.mutate({
      toGuideId: guide.id,
      toChapterId: guide.chapters[0].id,
      toPageId: generateUUID(),
      toGuideVersion: guide.version,
      guideTitle,
      toIndex: guide.chapters[0].pages.length - 1,
    });
  };

  return (
    <>
      <Inspector.Content>
        {/* Page Publishing Controls */}
        {!isLastHomepagePageActive && (
          <PublishAudiencePageToggle isPublished={isActivePagePublished} />
        )}

        {/* Homepage Settings */}
        {(canManagePortalSettings || hasHomePageManage) &&
          isLastHomepagePageActive && (
            <>
              <SetHomepageToggle />
              {isPageInPageEnabled && (
                <List.Item
                  thumbnail={<Thumbnail shape="square" icon={<IconTarget />} />}
                  checkedVariant="switch"
                  isChecked={targetedPagesEnabled}
                  onClick={handleToggleTargetedPages}
                >
                  Targeted pages
                </List.Item>
              )}
            </>
          )}

        <Inspector.Divider />

        {/* Title Editor */}
        <InspectorTitleEditor
          testId={isLastHomepagePageActive ? 'guideTitle' : 'pageTitle'}
          onEdit={
            isLastHomepagePageActive
              ? handleEditGuideTitle
              : handleEditPageTitle
          }
          propTitle={isLastHomepagePageActive ? guideTitle : pageTitle}
          subText="Title"
        />

        <Inspector.Divider />

        {/* Themeset Editor for Homepage */}
        {isLastHomepagePageActive && (
          <>
            <InspectorThemesetEditor guideId={guideId} />
            <Inspector.Divider />
          </>
        )}
      </Inspector.Content>

      {/* Delete Option for Non-Homepage Pages */}
      {isPageInPageEnabled && (
        <Inspector.Footer>
          <Tabs variant="clean">
            <Tabs.Tab
              icon={<IconCopy />}
              label="Duplicate"
              onClick={handleDuplicatePage}
            />
            {!isLastHomepagePageActive && (
              <Tabs.Tab
                icon={<IconDelete />}
                label="Delete"
                onClick={handleDeletePage}
                isDisabled={isLastHomepagePageActive}
              />
            )}
          </Tabs>
        </Inspector.Footer>
      )}
    </>
  );
};

export const LandingPageSettings = ({ isPublished, guideId, guideTitle }) => {
  const { editGuideTitle } = useGuideMutations(guideId);

  const handleEditGuideTitle = (guideTitle: string) => {
    if (!guideTitle) return;

    editGuideTitle.mutate({ guideTitle });
  };
  return (
    <>
      <Inspector.Content>
        <PublishLandingPageToggle isPublished={isPublished} />
        <InspectorTitleEditor
          testId="guideTitle"
          onEdit={handleEditGuideTitle}
          propTitle={guideTitle}
          subText="Title"
        />
        <Inspector.Divider />
        <InspectorThemesetEditor guideId={guideId} />
        <Inspector.Divider />
      </Inspector.Content>
    </>
  );
};
